import { format, isValid, parse, parseISO } from "date-fns";
import { id } from "date-fns/locale";
import { isDate, sortBy } from "es-toolkit";

const digits = new Intl.NumberFormat("id-ID", {
    style: "decimal",
});

// const rupiah = new Intl.NumberFormat("id-ID", {
//     style: "currency",
//     currency: "IDR",
//     maximumFractionDigits: 0,
// });

export function formatDigits(
    num: number | undefined,
): typeof num extends undefined ? undefined : string {
    if (num === undefined) return undefined as any;
    return digits.format(num);
}

export function formatDigitsUnit(
    num: number | undefined,
    units: {
        label: string;
        value: number;
    }[],
    opts: { zeroAsInfinite: boolean } = { zeroAsInfinite: false },
): typeof num extends undefined ? undefined : string {
    if (num === undefined) return undefined as any;
    const asc = sortBy(units, [(u) => u.value]);
    const desc = sortBy(units, [(u) => -u.value]);
    const unitValueLabels = new Map(units.map((u) => [u.value, u.label]));

    let unitModel = 0;
    let valueModel = num;

    if (num === 0) {
        if (opts.zeroAsInfinite) {
            return `∞`;
        } else {
            unitModel = asc[0].value;
            return `${valueModel} ${unitValueLabels.get(unitModel)}`;
        }
    } else {
        for (const unit of desc) {
            if (num % unit.value === 0) {
                unitModel = unit.value;
                valueModel = num / unit.value;
                break;
            }
        }
    }

    return `${valueModel} ${unitValueLabels.get(unitModel)}`;
}

export function formatRupiah(num: number | undefined): string {
    if (num === undefined) return "-";
    return "Rp. " + formatDigits(num);
}

export function formatDateISO(
    date: Date | undefined,
): typeof date extends undefined ? undefined : string {
    if (date === undefined) return undefined as any;
    return format(date, "yyyy-MM-dd");
}

export function formatDateVerbose(
    date: Date | undefined,
): typeof date extends undefined ? undefined : string {
    if (date === undefined) return undefined as any;
    return format(date, "dd MMMM yyyy", { locale: id });
}

export function formatDateTime(
    date: Date | undefined,
): typeof date extends undefined ? undefined : string {
    if (date === undefined) return undefined as any;
    return format(date, "dd/MM/yyyy HH:mm:ss", { locale: id });
}

export function formatDateTimeVerbose(
    date: Date | undefined,
): typeof date extends undefined ? undefined : string {
    if (date === undefined) return undefined as any;
    return format(date, "dd MMMM yyyy HH:mm:ss", { locale: id });
}

export function parseDate(
    date: string | Date | undefined,
): typeof date extends undefined ? undefined : Date {
    if (date === undefined) return undefined as any;
    if (isDate(date)) return date;
    const result = parseISO(date);
    if (!isValid(result)) throw new Error(`Invalid date ${date} -> ${result}`);
    return result;
}

export function parseTime(hour: number, minute: number): Date {
    return parse(`${hour}:${minute}`, "H:m", new Date());
}

export function formatUsageByte(num: number) {
    if (num >= 1_000_000_000) {
        return `${(num / 1_000_000_000).toFixed(1)} GB`;
    } else if (num >= 1_000_000) {
        return `${(num / 1_000_000).toFixed(1)} MB`;
    } else if (num >= 1_000) {
        return `${(num / 1_000).toFixed(1)} KB`;
    } else {
        return `${num} B`;
    }
}

export function formatUsageTime(num: number) {
    const texts = [];

    if (num >= 86400) {
        texts.push(`${Math.floor(num / 86400)} HARI`);
        num %= 86400;
    }

    if (num >= 3600) {
        texts.push(`${Math.floor(num / 3600)} JAM`);
        num %= 3600;
    }

    if (num >= 60) {
        texts.push(`${Math.floor(num / 60)} MENIT`);
        num %= 60;
    }

    texts.push(`${num} DETIK`);

    if (texts.length >= 2) {
        return texts.slice(0, 2).join(" ");
    }

    return texts[0];
}

/**
 * @param text `lat, lng`
 * @returns `[lng,lat]`
 */
export function parseCoordinate(
    text: string | undefined,
): typeof text extends undefined ? undefined : [number, number] {
    if (text === undefined) return undefined as any;
    const parts = text.split(", ").map((c) => parseFloat(c));
    return [parts[1], parts[0]];
}

/**
 * @param coordinates `[lng,lat]`
 * @returns `lat, lng`
 */
export function formatCoordinate(
    coordinates: [number, number] | undefined,
): typeof coordinates extends undefined ? undefined : string {
    if (coordinates === undefined) return undefined as any;
    return `${coordinates[1]}, ${coordinates[0]}`;
}

export function formatOpticalParam(
    num: number | undefined,
): typeof num extends undefined ? undefined : string {
    if (num === undefined) return undefined as any;
    return num.toFixed(2);
}
